import { useState } from 'react';
import { Switch, Typography, Empty } from 'antd';
import { OliCardTile } from './OliCardTile';
import { OliGroup } from '../../pages/KitStaging/boards/groupByOli';

interface Props {
	areaId: number;
	groups: OliGroup[];
	/** Accepts ``null`` so tapping an already-selected card deselects it
	 *  (same UX as Return Kit / shelf boards). */
	onSelect: (g: OliGroup | null) => void;
	selectedGroup?: OliGroup | null;
}

export function DekitAreaView({ areaId, groups, onSelect, selectedGroup }: Props) {
	const [showOrphans, setShowOrphans] = useState(false);
	const [showPickedUp, setShowPickedUp] = useState(false);
	// Click-toggles-selection — match Return Kit's behavior.
	const handleSelect = (g: OliGroup) => {
		onSelect(selectedGroup?.oli === g.oli ? null : g);
	};

	// A group counts as "picked up" if any of its cards has picked_up=true.
	const isPickedUp = (g: OliGroup) => g.kitCard?.picked_up === true || g.materialCard?.picked_up === true;

	const inArea = groups.filter((g) => g.job?.dekit_area_id === areaId);
	const orphans = groups.filter((g) => g.job?.dekit_area_id == null);

	// Both filters apply independently to both buckets (in-area and orphans).
	// Default: only active (not-yet-collected) in-area cards.
	const activeInArea = inArea.filter((g) => !isPickedUp(g));
	const pickedInArea = inArea.filter(isPickedUp);
	const activeOrphans = orphans.filter((g) => !isPickedUp(g));
	const pickedOrphans = orphans.filter(isPickedUp);

	const inAreaVisible = showPickedUp ? [...activeInArea, ...pickedInArea] : activeInArea;
	const orphansVisible = showOrphans ? (showPickedUp ? [...activeOrphans, ...pickedOrphans] : activeOrphans) : [];
	const visible = [...inAreaVisible, ...orphansVisible];

	// Counters reflect total in each category — what's currently hidden behind
	// each toggle (or what's revealed). Picked counter includes orphans only
	// when Show orphans is on, so the numbers match what flipping the toggle
	// would actually reveal in the current view.
	const pickedCount = pickedInArea.length + (showOrphans ? pickedOrphans.length : 0);
	const orphansCount = orphans.length;

	return (
		<div style={{ padding: 12 }}>
			<div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
				<Typography.Text strong>Cards in this Dekit area ({activeInArea.length})</Typography.Text>
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
					<Switch size="small" checked={showPickedUp} onChange={setShowPickedUp} aria-label="Show picked up" />
					<Typography.Text type="secondary" style={{ fontSize: 12 }}>
						Show picked up ({pickedCount})
					</Typography.Text>
				</span>
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
					<Switch size="small" checked={showOrphans} onChange={setShowOrphans} aria-label="Show orphans" />
					<Typography.Text type="secondary" style={{ fontSize: 12 }}>
						Show orphans ({orphansCount})
					</Typography.Text>
				</span>
			</div>

			{visible.length === 0 ? (
				<Empty style={{ marginTop: 32 }} description="No cards in this dekit area" />
			) : (
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
						gap: 8,
					}}
				>
					{visible.map((g) => (
						<div key={g.oli} style={{ flexShrink: 0 }}>
							<OliCardTile
								group={g}
								currentArea="dekit"
								selected={selectedGroup?.oli === g.oli}
								onSelect={handleSelect}
							/>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
